// hooks
import { Fragment, useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";

// routes
import { useNavigate } from "react-router-dom";

// material styles
import { Grid, Button, IconButton, TextField, Tooltip } from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";
import { Edit } from "@mui/icons-material";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import TableCM from "components/shared/Table";
import BackButton from "components/shared/BackButton";
import PaginationCM from "components/shared/Pagination";

// helper
import Toast from "helper/toast";
import GenerateHeadCell from "helper/generate-head-cell";

// styled component
import { StyledStack, StyledTitle, ButtonContainer } from "./addcategory.style";

// constants
import { PAGINATION_LIMIT } from "constants";

// request methods
import {
  addEducationCategoryRequest,
  getEducationCategoryRequest,
} from "api/requests/educations";

// validation
import { categorySchema } from "validations";

// category head cells
const headCells = GenerateHeadCell("education_category_list");

export default function AddEducationCategory() {
  const navigate = useNavigate();
  const { request, loading, setLoading } = useHttp(false);
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(categorySchema),
  });

  // page state for pagination
  const [page, setPage] = useState(1);

  // categories state
  const [categoryData, setCategoryData] = useState({
    data: [],
    count: 0,
  });

  // get all categories data
  const getCategoriesData = useCallback(async () => {
    setLoading(true);
    const res = await request({
      url: getEducationCategoryRequest,
      values: {
        skip: (page - 1) * PAGINATION_LIMIT,
        limit: PAGINATION_LIMIT,
        search: null,
      },
      isMulty: true,
    });
    setLoading(false);
    if (res) {
      if (res.data.count < 1) {
        return setCategoryData({
          count: res.data.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد" }],
        });
      }
      let transformedData = res.data.data.map((items) => ({
        id: items.id,
        title: items.title,
        edit: (
          <IconButton
            color="primary"
            onClick={() => {
              navigate("/education/category/edit/" + items.id);
            }}
          >
            <Tooltip title="ویرایش" placement="top">
              <Edit />
            </Tooltip>
          </IconButton>
        ),
      }));
      setCategoryData({
        data: transformedData,
        count: res.data.count,
      });
    }
  }, [page]);

  // add category
  const onSubmit = async (data) => {
    const res = await request({
      url: () => addEducationCategoryRequest({ ...data }),
    });

    if (res) {
      reset({
        title: "",
      });
      Toast({ type: "success", title: "ثبت سردسته " });
      getCategoriesData();
    }
    setLoading(false);
  };

  useEffect(() => {
    getCategoriesData();
  }, [getCategoriesData]);

  return (
    <DrawerCM title="افزودن سردسته آموزش">
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <Grid container>
            <Grid item mt={3} xs={12}>
              <BackButton Route="/education" />
            </Grid>
            <StyledStack>
              <Grid>
                <StyledTitle>افزودن سردسته</StyledTitle>
                <Grid container>
                  <Grid item md={10} sm={9} xs={12} p={1.5}>
                    <TextField
                      label="عنوان سردسته"
                      variant="outlined"
                      fullWidth
                      id="title"
                      {...register("title")}
                      error={Boolean(errors.title)}
                      helperText={errors.title?.message}
                    />
                  </Grid>
                  <ButtonContainer item md={2} sm={3} xs={12}>
                    <Button
                      color="success"
                      startIcon={<CheckIcon />}
                      variant="contained"
                      sx={{ mt: 1, mr: 1 }}
                      onClick={handleSubmit(onSubmit)}
                    >
                      ثبت
                    </Button>
                  </ButtonContainer>
                </Grid>
              </Grid>
            </StyledStack>
          </Grid>

          <Grid container>
            <Grid item xs={12} my={2}>
              <TableCM
                title="لیست سردسته ها"
                rows={categoryData?.data}
                headCells={headCells}
                page={page}
                colspan={categoryData?.count === 0 ? "11" : ""}
                fontSize="12px"
                cellFontSize="12px"
              />
            </Grid>
            {categoryData.count < 1 ? (
              ""
            ) : (
              <PaginationCM
                handleChange={(_, val) => setPage(val)}
                page={page}
                count={categoryData.count}
              />
            )}
          </Grid>
        </Fragment>
      )}
    </DrawerCM>
  );
}
